import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Placeholder } from '../../commons/components';
import { getDateFromString } from '../../../utils/helpers';
import {
  getIndividualDetails,
  reset30DaysIndividualDetailsState,
  saveDateRangeFor30DaysIndividual,
} from '../store/actions';
import { filterType } from '../../../constants/filterOptions';
import TableComponent from '../../commons/components/TableComponent';

const getDivisorValue = (field) => {
  if (field === 'total_steps') {
    return 1000000;
  }
  return null;
};

const IndividualDetailsLast30DaysContainer = ({
  field,
  companySelected,
  startValue,
  endValue,
  selectedFilteredType,
  onClearKpiState,
}) => {
  const corporateList = useSelector((state) => state.kpi.corporateList);
  const corpIdNameMap = useSelector((state) => state.kpi.cropIdNameMap);
  const freemiumCropIdNameMap = useSelector((state) => state.kpi.freemiumCropIdNameMap);
  const last30Days = useSelector((state) => state.kpi.individualDetailsLast30Days);
  const rows = last30Days && last30Days.data && last30Days.data[field];
  const dateRange = last30Days?.dateRange?.[field];
  const dispatch = useDispatch();

  useEffect(() => {
    if (startValue && endValue) {
      dispatch(
        saveDateRangeFor30DaysIndividual({
          [field]: {
            ...(dateRange || {}),
            startDate: format(new Date(startValue), 'yyyy-MM-dd'),
            endDate: format(new Date(endValue), 'yyyy-MM-dd'),
          },
        })
      );
    }
  }, [startValue, endValue, selectedFilteredType]);

  function getPayload() {
    return {
      companyIdList: companySelected.map((c) => c._id),
      startDate: format(new Date(startValue), 'yyyy-MM-dd'),
      endDate: format(new Date(endValue), 'yyyy-MM-dd'),
      viewedFrom: 'kpi',
      quarterly: false,
      field: field,
      selectedFilterType: filterType.last30Days,
    };
  }

  useEffect(() => {
    if (
      !Array.isArray(companySelected) ||
      companySelected.length !== 1 ||
      !startValue ||
      !endValue
    ) {
      return;
    }

    if (
      rows &&
      rows.length > 0 &&
      format(new Date(startValue), 'yyyy-MM-dd') === dateRange.oldStartDate &&
      format(new Date(endValue), 'yyyy-MM-dd') === dateRange.oldEndDate
    ) {
      /** data exist. no need to fetch */
      return;
    }
    fetchData();
    dispatch(
      saveDateRangeFor30DaysIndividual({
        [field]: {
          ...dateRange,
          oldStartDate: format(new Date(startValue), 'yyyy-MM-dd'),
          oldEndDate: format(new Date(endValue), 'yyyy-MM-dd'),
        },
      })
    );
  }, [companySelected, startValue, endValue]);

  const divisor = getDivisorValue(field);

  const getHeaderSubtitle = () => {
    if (startValue && endValue) {
      const sDate = getDateFromString(startValue);
      const eDate = getDateFromString(endValue);
      return `${format(sDate, 'do MMM')} - ${format(eDate, 'do MMM yyyy')}`;
    }
  };

  // Define columns for the table
  const columns = useMemo(
    () => [
      {
        Header: 'Enterprise Name',
        accessor: 'corporate_name',
      },
      {
        Header: (
          <div className='d-flex flex-column'>
            <span>Last 30 Days</span>
            {getHeaderSubtitle() && (
              <span className='subtitle'>
                {'('}
                {getHeaderSubtitle()}
                {')'}
              </span>
            )}
          </div>
        ),
        accessor: field,
        Cell: ({ cell }) =>
          last30Days.loading ? (
            <Placeholder width='90%' height={20} />
          ) : divisor ? (
            (cell.value / divisor).toFixed(2)
          ) : (
            cell.value?.toLocaleString('en-US')
          ),
      },
    ],
    [field, last30Days.loading]
  );

  const data = useMemo(() => {
    if (!rows || rows.length === 0) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        corporate_name: <Placeholder width='90%' height={20} />,
        [field]: <Placeholder width='90%' height={20} />,
      }));
      return placeholderRows;
    }

    let corporateWithActivity = rows;
    let activeCorporateIds = corporateWithActivity.map((corporate) => corporate.corporate_id);
    let corporateWithoutActivity = corporateList.data.filter(
      (corporate) => !activeCorporateIds.includes(corporate._id)
    );

    let copyRows = corporateWithActivity.map((item) => ({
      corporate_id: item.corporate_id,
      corporate_name: corpIdNameMap[item.corporate_id] || freemiumCropIdNameMap[item.corporate_id],
      [field]: item[field] || 0,
    }));

    corporateWithoutActivity.forEach((corporate) => {
      copyRows.push({
        corporate_id: corporate._id,
        corporate_name:
          corporate.entity_name ||
          corpIdNameMap[corporate._id] ||
          freemiumCropIdNameMap[corporate._id],
        [field]: 0,
      });
    });

    return copyRows.filter(
      (item) => companySelected[0]?._id === 'ALL' || item.corporate_id === companySelected[0]?._id
    );
  }, [field, rows, companySelected, freemiumCropIdNameMap]);

  function fetchData() {
    const payload = getPayload();
    dispatch(getIndividualDetails(payload));
  }

  return (
    <>
      <button
        onClick={() => {
          onClearKpiState();
        }}
        className='btn btn-primary hard-reload'
      >
        Refresh
      </button>
      <TableComponent columns={columns} data={data} />
    </>
  );
};

export default IndividualDetailsLast30DaysContainer;
